import { useTranslation } from 'react-i18next';

import { CompaniesLogosHeader } from '@/components/shared/CompaniesLogosHeader';
import { BrandingJumbotron } from '@/components/shared/BrandingJumbotron';
import { UserAuthPageLayout } from '@/components/App/AppLayout/LoginPage/UserAuthPageLayout';
import { LoginManagementHeader } from '@/components/shared/LoginManagementHeader';
import { Button } from '@/components/shared/buttons';
import { useIsOnInstance } from '@/hooks/shared/useIsOnInstance';
import { useBranding } from '@/hooks/branding/useBranding';
import { useLoginPopup } from '@/hooks/user/useLoginPopup';

import style from './LoginPage.module.sass';

type LoginPageProps = {
  isUserNotAuthorisedForThisCompanyError?: boolean;
};

export const LoginPage = ({
  isUserNotAuthorisedForThisCompanyError,
}: LoginPageProps) => {
  const { branding } = useBranding();
  const { t } = useTranslation();
  const { isAdminInstance } = useIsOnInstance();
  const openLoginPopup = useLoginPopup();

  if (!branding) {
    return null;
  }

  // A normal login runs Envoy's oauth2 handshake in a popup; the popup signals
  // completion over a BroadcastChannel, the opener refetches the session, and the
  // app tab stays mounted. A not-authorised user signs out first — a full-page
  // navigation, to reset the whole app — so they can pick a different account.
  const handleLogin = () => {
    if (isUserNotAuthorisedForThisCompanyError) {
      window.location.assign('/auth/signout');

      return;
    }

    openLoginPopup();
  };

  const renderBrandHeader = () => {
    if (!isAdminInstance) {
      return <CompaniesLogosHeader />;
    }

    return (
      <div className={style.brandingJumbotronWrapper}>
        <BrandingJumbotron />
      </div>
    );
  };

  return (
    <UserAuthPageLayout>
      {renderBrandHeader()}
      <LoginManagementHeader
        title={t('loginPage:welcomeBack')}
        subtitle={isUserNotAuthorisedForThisCompanyError
          ? t('loginPage:errors.userNotAuthorisedForThisCompany', 'You are not authorised to access this company.')
          : t('loginPage:pleaseSignIn')}
      />
      <Button
        variant='primary'
        width='full'
        onClick={handleLogin}>
        {isUserNotAuthorisedForThisCompanyError
          ? t('loginPage:login.switchAccount', 'Sign in with a different account')
          : t('loginPage:login.login', 'Login')}
      </Button>
    </UserAuthPageLayout>
  );
};
